import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Shell } from "../components/Shell";
import { ApiError, tenants } from "../lib/api";
import type { SLARow, SLAResponse, Tenant } from "../lib/api";

const TIPO_LABEL: Record<string, string> = {
  peticion: "Petición",
  queja: "Queja",
  reclamo: "Reclamo",
  denuncia: "Denuncia",
  sugerencia: "Sugerencia",
  felicitacion: "Felicitación",
};
const PRIO_LABEL: Record<string, string> = {
  baja: "Baja",
  normal: "Normal",
  alta: "Alta",
  urgente: "Urgente",
};

export function TenantSLAPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [cfg, setCfg] = useState<SLAResponse | null>(null);
  const [draft, setDraft] = useState<SLARow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2800);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([tenants.get(id), tenants.getSLA(id)])
      .then(([t, c]) => {
        setTenant(t);
        setCfg(c);
        setDraft(c.tabla);
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "No se pudo cargar"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  const map = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of draft) m.set(`${r.tipo}|${r.prioridad}`, r.dias_habiles);
    return m;
  }, [draft]);

  const setCell = (tipo: string, prioridad: string, dias: number) => {
    setDraft(
      draft.map((r) =>
        r.tipo === tipo && r.prioridad === prioridad
          ? { ...r, dias_habiles: dias }
          : r,
      ),
    );
  };

  const save = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const r = await tenants.updateSLA(id, draft);
      setCfg(r);
      setDraft(r.tabla);
      flash("ok", "SLA guardado");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  if (error || !tenant || !cfg) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "Tenant no encontrado."}</div>
          <p>
            <Link to="/tenants">← Volver</Link>
          </p>
        </section>
      </Shell>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(cfg.tabla);

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to={`/tenants/${tenant.id}`}>← {tenant.nombre}</Link> · SLA
        </div>
        <h1 style={{ fontSize: 28, marginTop: 6 }}>Plazos de respuesta (SLA)</h1>
        <p className="lede" style={{ maxWidth: 720 }}>
          Plazos en días hábiles para cada combinación tipo × prioridad. El
          valor NUNCA excede el máximo legal de Ley 1755/2015. Para reducir
          el plazo (alcaldía más estricta), pon un número menor.
        </p>

        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-inner" style={{ overflowX: "auto" }}>
            <table className="sla-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Tipo</th>
                  {cfg.prioridades.map((p) => (
                    <th key={p}>{PRIO_LABEL[p] ?? p}</th>
                  ))}
                  <th className="sla-legal">Máx legal</th>
                </tr>
              </thead>
              <tbody>
                {cfg.tipos.map((t) => {
                  const legal = cfg.legal_max[t] ?? 15;
                  return (
                    <tr key={t}>
                      <td className="sla-tipo">{TIPO_LABEL[t] ?? t}</td>
                      {cfg.prioridades.map((p) => {
                        const dias = map.get(`${t}|${p}`) ?? legal;
                        const over = dias > legal;
                        return (
                          <td key={p}>
                            <input
                              type="number"
                              className={`sla-input${over ? " is-over" : ""}`}
                              min={1}
                              max={legal}
                              value={dias}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                if (Number.isFinite(v)) setCell(t, p, v);
                              }}
                            />
                          </td>
                        );
                      })}
                      <td className="sla-legal">{legal} d</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ marginTop: 22, display: "flex", gap: 10 }}>
          <button
            type="button"
            className="cta"
            style={{ maxWidth: 280 }}
            disabled={saving || !dirty}
            onClick={() => void save()}
          >
            <span className="cta-inner">
              {saving ? "Guardando…" : "Guardar SLA"}
            </span>
          </button>
          {dirty && (
            <button
              type="button"
              className="btn-outline"
              disabled={saving}
              onClick={() => setDraft(cfg.tabla)}
            >
              Descartar
            </button>
          )}
        </div>

        {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
      </section>
    </Shell>
  );
}
